import { useState } from 'react'
import { useRuns } from '../hooks/useRuns'
import { runStatus, availableRemaining } from '../lib/run'
import { todayKey } from '../lib/datetime'
import { Sheet } from './UI'
import { ClapboardClosed } from './ClapboardIcon'

// ── RunSheet17 ─────────────────────────────────────────────────────────────────
// "The Run", on the 17 language. A self-paced binge plan for a show with a backlog:
// pick a nightly pace, and each night the run hands you tonight's slice with one
// button that puts every episode of it on Tonight. Same runs table, same maths
// (lib/run.js), same plans; only the chrome changed. Jason: "I used that frequently
// to save taps on adding episodes to watch."

const EYE = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)' }
const pretty = (s) => { try { return new Date(s + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) } catch { return s } }

export function RunSheet17({ show, progress, user, plans, onClose, onToast }) {
  const { runs, enabled, start, end } = useRuns(user)
  const today = todayKey()
  const run = runs?.[show?.id]
  const [pace, setPace] = useState(2)
  const [busy, setBusy] = useState(false)
  const [added, setAdded] = useState(false)
  const remaining = availableRemaining(show, progress, today)
  const st = run ? runStatus(run, show, progress || { season: 1, episode: 1 }, today) : null

  const begin = async () => {
    if (busy) return; setBusy(true)
    try { await start(show.id, { mode: 'pace', pace, show, progress: progress || { season: 1, episode: 1 } }); onToast?.(`Run started · ${pace} a night`) }
    finally { setBusy(false) }
  }
  const addSlice = async () => {
    if (!st?.slice || added || busy) return; setBusy(true)
    try {
      for (const ep of st.slice.episodes) {
        if (!plans?.isPlanned?.(show.id, ep.season, ep.episode)) await plans?.toggle?.(show, ep.season, ep.episode, null)
      }
      setAdded(true); onToast?.(`${st.slice.count} episode${st.slice.count === 1 ? '' : 's'} on Tonight`)
    } finally { setBusy(false) }
  }
  const stop = async () => { if (busy) return; setBusy(true); try { await end(show.id, 'abandoned'); onToast?.('Run ended'); onClose?.() } finally { setBusy(false) } }

  const pill = (on, label, onClick, key) => (
    <button key={key ?? label} onClick={onClick} className="ui-hit" style={{ height: 36, padding: '0 14px', borderRadius: 'var(--radius-pill)', border: `1px solid ${on ? 'var(--amber)' : 'var(--border)'}`, background: on ? 'var(--amber)' : 'var(--bg-card)', color: on ? 'var(--amber-ink)' : 'var(--text)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>{label}</button>
  )

  return (
    <Sheet open onClose={onClose} label="The run" from="bottom" panelStyle={{ padding: '10px 20px calc(30px + env(safe-area-inset-bottom))', background: 'var(--bg-raised)' }}>
      <div style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 16px' }} />
      <div style={EYE}>{run ? (st?.standing === 'ahead' ? 'Ahead of pace' : st?.standing === 'behind' ? 'Behind pace' : 'On pace') : 'The run'}</div>
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 500, color: 'var(--text-strong)', lineHeight: 1.15, marginTop: 4 }}>{show.name}</div>

      {!enabled && <div style={{ color: 'var(--text-dim)', fontSize: 13.5, marginTop: 14 }}>Runs aren’t available right now.</div>}

      {enabled && !run && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.5 }}>{remaining} episode{remaining === 1 ? '' : 's'} waiting. Pick a pace and each night the run puts that many on Tonight in one tap.</div>
          <div style={{ ...EYE, margin: '16px 0 10px' }}>A night</div>
          <div style={{ display: 'flex', gap: 8 }}>{[1, 2, 3, 5].map(n => pill(pace === n, `${n}`, () => setPace(n), n))}</div>
          <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-dim)' }}>Done in about {Math.max(1, Math.ceil(remaining / pace))} night{Math.ceil(remaining / pace) === 1 ? '' : 's'}.</div>
          <button onClick={begin} disabled={busy || remaining === 0} className="ui-hit" style={{ marginTop: 18, width: '100%', height: 46, borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--amber)', color: 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Start the run</button>
        </div>
      )}

      {enabled && run && st && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            {[['Left', st.remaining], ['A night', st.pace], ['Done by', st.done ? 'Done' : pretty(st.projectedFinish)]].map(([k, v]) => (
              <div key={k} style={{ padding: '12px 0', borderTop: '1px solid var(--border)' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 16, color: 'var(--text-strong)' }}>{v}</div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>{k}</div>
              </div>
            ))}
          </div>
          {st.slice && (
            <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 'var(--radius-md)', background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <div style={EYE}>Tonight’s slice</div>
              <div style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 500, color: 'var(--text-strong)', marginTop: 4 }}>{st.slice.label}</div>
              <button onClick={addSlice} disabled={added || busy} className="ui-hit" style={{ marginTop: 12, width: '100%', height: 44, borderRadius: 'var(--radius-pill)', border: `1px solid ${added ? 'var(--border)' : 'var(--amber)'}`, background: added ? 'var(--bg-card)' : 'var(--amber)', color: added ? 'var(--text-muted)' : 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 14.5, fontWeight: 600, cursor: added ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {added ? <><ClapboardClosed size={15} color="var(--text-muted)" />On Tonight</> : `Put ${st.slice.count} on Tonight`}
              </button>
            </div>
          )}
          {st.done && <div style={{ marginTop: 14, fontSize: 14, color: 'var(--text-muted)' }}>Nothing left to run. Nice.</div>}
          <button onClick={stop} disabled={busy} style={{ marginTop: 8, minHeight: 44, background: 'none', border: 'none', color: 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer', padding: '6px 0' }}>End the run</button>
        </div>
      )}
    </Sheet>
  )
}
